// import { /* StoreApi removed - was only a type */ } from 'zustand';
// Removed type imports: BaseState, StoreConfig

// Create a global identifier that any module can reference
const GLOBAL_STORE_MANAGER_KEY = '__FEDERATED_STORE_MANAGER__';

class StoreManager {
  static instance;
  // Make stores public so we can inspect it
  stores = new Map();

  // Map<storeId, Map<tabId, state>>
  tabStateCache = new Map();

  constructor() {
    this.loadPersistedStores();
    // setInterval(() => this.cleanupExpiredStores(), 60000);
    window.addEventListener('beforeunload', () => this.handleBeforeUnload());
  }

  static getInstance() {
    // First check if it exists in the global scope
    if (typeof window !== 'undefined' && window[GLOBAL_STORE_MANAGER_KEY]) {
      return window[GLOBAL_STORE_MANAGER_KEY];
    }

    // If not, create a new instance
    if (!StoreManager.instance) {
      StoreManager.instance = new StoreManager();

      // Store it globally to ensure it's a singleton across module boundaries
      if (typeof window !== 'undefined') {
        window[GLOBAL_STORE_MANAGER_KEY] = StoreManager.instance;
      }
    }

    return StoreManager.instance;
  }

  isGlobalStore(storeId) {
    const entry = this.stores.get(storeId);
    return entry?.config?.cache?.scope === 'global';
  }

  getTabState(storeId, tabId) {
    if (this.isGlobalStore(storeId)) {
      return null;
    }
    return this.tabStateCache.get(storeId)?.get(tabId) || null;
  }

  setTabState(storeId, tabId, state) {
    // Global stores don't use tab state cache
    if (this.isGlobalStore(storeId)) {
      return;
    }
    let storeCache = this.tabStateCache.get(storeId);
    if (!storeCache) {
      storeCache = new Map();
      this.tabStateCache.set(storeId, storeCache);
    }
    storeCache.set(tabId, state);
  }

  dumpTabStateCaches() {
    console.log('=== STORE MANAGER TAB STATE CACHE DUMP ===');
    let totalEntries = 0;

    this.tabStateCache.forEach((tabStates, storeId) => {
      const tabIds = Array.from(tabStates.keys());
      console.log(`Store ${storeId} has tab states for: ${tabIds.join(', ')}`);
      totalEntries += tabIds.length;
    });

    console.log(`Total entries across all stores: ${totalEntries}`);
    console.log('=== END DUMP ===');
  }

  clearTabState(storeId, tabId) {
    // Global stores don't use tab state cache
    if (this.isGlobalStore(storeId)) {
      return;
    }
    this.tabStateCache.get(storeId)?.delete(tabId);
  }

  // New method to clear all stores for a specific tab
  clearAllStoresForTab(tabId) {
    const clearedStores = new Set();
    // Iterate through all stores and clear this tab's state
    this.tabStateCache.forEach((tabStates, storeId) => {
      if (this.isGlobalStore(storeId)) {
        return;
      }
      const hadTab = tabStates.has(tabId);
      tabStates.delete(tabId);
      if (hadTab) {
        clearedStores.add(storeId);
      }
    });

    // Also reset any store that's currently using this tab's state
    this.stores.forEach(({ store, config }, storeId) => {
      // Skip global stores - they don't get reset per tab
      if (config.cache.scope === 'global') {
        return;
      }
      const state = store.getState();

      // If this store is currently using this tab ID or we previously cleared its cache
      if (clearedStores.has(storeId)) {
        console.log(`Resetting store ${storeId} that was using tab ${tabId}`);

        // Reset to initial state
        store.setState({
          ...config.initialState,
          _metadata: {
            lastAccessed: Date.now(),
            lastUpdated: Date.now(),
            tabId: null,
            lastResetTab: null
          }
        });
      }
    });
  }

  registerStore(id, store, config) {
    this.stores.set(id, { store, config });
    this.loadPersistedState(id);
  }

  loadPersistedState(storeId) {
    const entry = this.stores.get(storeId);
    if (!entry) return;

    const { store, config } = entry;
    if (config.cache.strategy !== 'memory') {
      const storage =
        config.cache.strategy === 'persistent' ? localStorage : sessionStorage;

      const savedState = storage.getItem(`store_${storeId}`);
      if (savedState) {
        store.setState(JSON.parse(savedState));
      }
    }
  }

  loadPersistedStores() {
    this.stores.forEach((_, id) => this.loadPersistedState(id));
  }

  persistStore(storeId) {
    const entry = this.stores.get(storeId);
    if (!entry) return;

    const { store, config } = entry;
    if (config.cache.strategy !== 'memory') {
      const storage =
        config.cache.strategy === 'persistent' ? localStorage : sessionStorage;

      storage.setItem(`store_${storeId}`, JSON.stringify(store.getState()));
    }
  }

  handleBeforeUnload() {
    this.stores.forEach((entry, id) => {
      if (!entry.config.cache.clearOnRefresh) {
        this.persistStore(id);
      }
    });
  }

  cleanupExpiredStores() {
    this.stores.forEach(({ store, config }, id) => {
      if (config.cache.expiryTime > 0) {
        const state = store.getState();
        const lastAccessed = state._metadata?.lastAccessed || Date.now();
        const expiryTime = config.cache.expiryTime * 60 * 1000;

        if (Date.now() - lastAccessed > expiryTime) {
          store.setState(config.initialState);
          if (config.cache.strategy !== 'memory') {
            const storage =
              config.cache.strategy === 'persistent'
                ? localStorage
                : sessionStorage;
            storage.removeItem(`store_${id}`);
          }
        }
      }
    });
  }

  updateStoresTabId(newTabId) {
    this.stores.forEach(({ store, config }) => {
      // Skip global stores - they don't participate in tab switching
      if (config.cache.scope === 'global') {
        return;
      }
      const state = store.getState();
      const currentTabId = state._metadata?.tabId;
      const lastResetTab = state._metadata?.lastResetTab;

      // Only proceed if we're switching to a different tab
      if (currentTabId !== newTabId) {
        switch (config.cache.tabBehavior) {
          case 'reset':
            // Only reset if we haven't reset for this tab before
            if (lastResetTab !== newTabId) {
              store.setState({
                ...config.initialState,
                _metadata: {
                  lastAccessed: Date.now(),
                  lastUpdated: Date.now(),
                  tabId: newTabId,
                  lastResetTab: newTabId // Track that we've reset for this tab
                }
              });
            } else {
              // Just update the tab ID and timestamps
              store.setState({
                ...state,
                _metadata: {
                  ...state._metadata,
                  lastAccessed: Date.now(),
                  tabId: newTabId
                }
              });
            }
            break;

          case 'persist':
            // Load the cached state for the new tab if it exists, otherwise use initial state
            const cachedState = this.getTabState(config.id, newTabId);
            const targetState = cachedState || config.initialState;

            store.setState({
              ...targetState,
              _metadata: {
                ...targetState._metadata,
                lastAccessed: Date.now(),
                lastUpdated: Date.now(),
                tabId: newTabId
              }
            });
            break;
        }
      }
    });
  }
}

export const storeManager = StoreManager.getInstance();
